import React, { useEffect, useState, useMemo } from 'react';
import { API } from './constant';
import axios from 'axios';
import Header from './Header';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Bar, Line, Scatter, Bubble } from 'react-chartjs-2';
import { extractFilters, processDashboardData, exportToCSV } from './dashboardUtils';

ChartJS.register(...registerables);

const Dashboard = () => {
    const [loggedinRole, setLoggedinRole] = useState("");
    const [loggedinLocation, setLoggedinLocation] = useState("");
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        school: "",
        class: "",
        session: "",
        quarter: "",
        message: "",
        search: ""
    });

    // useEffect(() => {
    //     const fetchUserData = async () => {
    //         try {
    //             const res = await axios.get(`${API}/login/logged-in-user`, {
    //                 headers: {
    //                     "Content-Type": "application/json",
    //                     "token": localStorage.getItem("token")
    //                 }
    //             });
    //             setLoggedinRole(res.data.data.role);
    //             setLoggedinLocation(res.data.data.school_location);
    //         } catch (err) {
    //             if (err.response && (err.response.status === 401 || err.response.status === 302)) {
    //                 localStorage.removeItem("token");
    //                 window.location.href = "/";
    //             }
    //         }
    //     };

    //     const fetchStudents = async () => {
    //         try {
    //             const res = await axios.get(`${API}/upload`);
    //             setStudents(res.data.data);
    //         } catch (err) {
    //             console.error("Error fetching students", err);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchUserData();
    //     fetchStudents();
    // }, []);



    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const res = await axios.get(`${API}/login/logged-in-user`, {
                    headers: {
                        "Content-Type": "application/json",
                        "token": localStorage.getItem("token")
                    }
                });

                const userRole = res.data.data.role;
                const userLocation = res.data.data.school_location;

                setLoggedinRole(userRole);
                setLoggedinLocation(userLocation);

                // Fetch students with proper filtering based on role
                await fetchStudents(userRole, userLocation);

            } catch (err) {
                console.error("Error fetching user data:", err);
                if (err.response && (err.response.status === 401 || err.response.status === 302)) {
                    localStorage.removeItem("token");
                    window.location.href = "/";
                }
            }
        };

        const fetchStudents = async (role, location) => {
            try {
                let url = `${API}/upload`;

                // Add query parameters based on role
                const params = new URLSearchParams();

                if (role === "Admin" && location) {
                    params.append("school_location", location);
                }
                // Super Admin: no filter (sees everything)
                // User / other roles: we can either send a flag or just return empty

                const res = await axios.get(`${url}?${params.toString()}`);

                setStudents(res.data.data || []);
            } catch (err) {
                console.error("Error fetching students", err);
                setStudents([]); // safety
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []);
    const filterOptions = useMemo(() => extractFilters(students), [students]);
    const analytics = useMemo(() => processDashboardData(students, filters, loggedinRole, loggedinLocation), [students, filters, loggedinRole, loggedinLocation]);

    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 uppercase font-black tracking-widest text-gray-400 animate-pulse">
            Processing Analytics Engine...
        </div>
    );

    const kpi = analytics.kpi;
    const commonChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8, font: { weight: 'bold', size: 10 } } }
        },
        scales: {
            y: { grid: { color: '#f3f4f6', drawBorder: false }, border: { display: false } },
            x: { grid: { display: false }, border: { display: false } }
        }
    };

    const ExportBtn = ({ data, filename, className = "absolute top-4 right-4 z-10" }) => (
        <button
            onClick={() => exportToCSV(data, filename)}
            className={`${className} p-1.5 text-slate-400 hover:text-indigo-600 bg-white hover:bg-indigo-50 border border-slate-200 hover:border-indigo-100 rounded-md transition-all shadow-sm`}
            title="Export Data to CSV"
        >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
        </button>
    );

    return (
        <div className="bg-[#f8fafc] min-h-screen flex flex-col font-sans">
            <Header />
            <div className="p-4 md:p-8 max-w-[1400px] mx-auto w-full flex-1 mt-[80px] md:mt-[120px] ">

                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight leading-none uppercase">Lexile Analytics</h1>
                        <p className="text-gray-500 mt-2 font-medium tracking-wide">Data-driven Reading Excellence</p>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-8 flex flex-wrap gap-4 items-center">
                    <div className="w-full md:w-auto flex-1 min-w-[200px]">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Search Student</label>
                        <input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Name or ERP ID..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
                    </div>
                    {/* {[
                        { label: "School Location", name: "school", options: filterOptions.schools },
                        { label: "Class", name: "class", options: filterOptions.classes },
                        { label: "Session", name: "session", options: filterOptions.sessions },
                        { label: "Quarter", name: "quarter", options: filterOptions.quarters },
                        { label: "Reading Status", name: "message", options: filterOptions.messages }
                    ].map((f, i) => (
                        <div key={i} className="min-w-[140px] md:flex-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">{f.label}</label>
                            <select name={f.name} value={filters[f.name]} onChange={handleFilterChange} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer">
                                <option value="">All</option>
                                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                    ))} */}
                    {[
                        { label: "School Location", name: "school", options: filterOptions.schools },
                        { label: "Class", name: "class", options: filterOptions.classes },
                        { label: "Session", name: "session", options: filterOptions.sessions },
                        { label: "Quarter", name: "quarter", options: filterOptions.quarters },
                        { label: "Reading Status", name: "message", options: filterOptions.messages }
                    ].map((f, i) => {
                        const isSchoolFilter = f.name === "school";
                        const isAdmin = loggedinRole === "Admin";
                        const isSuperAdmin = loggedinRole === "Super Admin";   // Make sure this matches your exact role name

                        let displayOptions = f.options;
                        let selectedValue = filters[f.name];
                        let isDisabled = false;

                        // Special handling for School Location when user is Admin
                        if (isSchoolFilter && isAdmin && loggedinLocation) {
                            displayOptions = [loggedinLocation];   // Only show their own location
                            selectedValue = loggedinLocation;      // Force select their location
                            isDisabled = true;                     // Disable the dropdown
                        }

                        return (
                            <div key={i} className="min-w-[140px] md:flex-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">
                                    {f.label}
                                </label>

                                <select
                                    name={f.name}
                                    value={selectedValue}
                                    onChange={handleFilterChange}
                                    disabled={isDisabled}
                                    className={`w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-800 
                    focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer
                    ${isDisabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
                                >
                                    <option value="">All</option>
                                    {displayOptions.map(o => (
                                        <option key={o} value={o}>
                                            {o}
                                        </option>
                                    ))}
                                </select>

                                {/* Optional: Show a small note for Admin */}
                                {isSchoolFilter && isAdmin && loggedinLocation && (
                                    <p className="text-[10px] text-slate-500 mt-1">
                                        Showing data for: <span className="font-medium">{loggedinLocation}</span>
                                    </p>
                                )}
                            </div>
                        );
                    })}
                    <div className="mt-5">
                        <button onClick={() => setFilters({ school: '', class: '', session: '', quarter: '', message: '', search: '' })} className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-black uppercase tracking-wider transition-colors">Reset</button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8 ">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative overflow-hidden group">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="lexile_avg_assessments" />
                        <div className="absolute -right-6 -top-6 bg-indigo-50 h-24 w-24 rounded-full group-hover:scale-150 transition-transform duration-500 opacity-50"></div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2 relative z-10 w-4/5">Avg Lexile Level</p>
                        <p className="text-4xl font-black text-indigo-600 relative z-10 tracking-tighter">{kpi.avgScore} <span className="text-lg text-indigo-300">L</span></p>
                        <p className="text-xs text-slate-400 mt-2 font-bold tracking-wide relative z-10">Based on {kpi.totalAssessed} assessments</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative overflow-hidden group">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="speed_avg_assessments" />
                        <div className="absolute -right-6 -top-6 bg-teal-50 h-24 w-24 rounded-full group-hover:scale-150 transition-transform duration-500 opacity-50"></div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2 relative z-10 w-4/5">Avg Reading Speed</p>
                        <p className="text-4xl font-black text-teal-600 relative z-10 tracking-tighter">{kpi.avgSpeed} <span className="text-lg text-teal-300">WPM</span></p>
                        <p className="text-xs text-slate-400 mt-2 font-bold tracking-wide relative z-10">Based on {kpi.totalAssessed} assessments</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative overflow-hidden group">
                        <ExportBtn data={analytics.exportData.improvementData} filename="improvement_data" />
                        <div className="absolute -right-6 -top-6 bg-emerald-50 h-24 w-24 rounded-full group-hover:scale-150 transition-transform duration-500 opacity-50"></div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2 relative z-10 w-4/5">Score Improvement</p>
                        <p className="text-4xl font-black text-emerald-600 relative z-10 tracking-tighter">{kpi.improvedPercent}%</p>
                        <p className="text-xs text-slate-400 mt-2 font-bold tracking-wide relative z-10">Current vs Prev Qtr</p>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative overflow-hidden group">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="well_matched_books" />
                        <div className="absolute -right-6 -top-6 bg-amber-50 h-24 w-24 rounded-full group-hover:scale-150 transition-transform duration-500 opacity-50"></div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2 relative z-10 w-4/5">Well-Matched Books</p>
                        <p className="text-4xl font-black text-amber-500 relative z-10 tracking-tighter">{kpi.wellMatchedPercent}%</p>
                        <p className="text-xs text-slate-400 mt-2 font-bold tracking-wide relative z-10">Within ±100L Range</p>
                    </div>
                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-center relative">
                        <ExportBtn className="absolute top-2 right-[65px] " data={analytics.exportData.baseAssessments} filename="reader_categories" />
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-[9px] font-bold text-slate-400 uppercase">Outstanding:</span>
                            <span className="text-sm font-black text-indigo-600">{kpi.messageCounts["Outstanding Reader"] || 0}</span>
                        </div>
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-[9px] font-bold text-slate-400 uppercase">Very Good:</span>
                            <span className="text-sm font-black text-blue-500">{kpi.messageCounts["Very Good Reader"] || 0}</span>
                        </div>
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-[9px] font-bold text-slate-400 uppercase">Good:</span>
                            <span className="text-sm font-black text-emerald-600">{kpi.messageCounts["Good Reader"] || 0}</span>
                        </div>
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-[9px] font-bold text-slate-400 uppercase">Needs Impr.:</span>
                            <span className="text-sm font-black text-amber-500">{kpi.messageCounts["Reading needs improvement"] || 0}</span>
                        </div>
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-[9px] font-bold text-slate-400 uppercase">Prioritized:</span>
                            <span className="text-sm font-black text-orange-500">{kpi.messageCounts["Reading to be Prioritized"] || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[9px] font-bold text-slate-400 uppercase">Poor:</span>
                            <span className="text-sm font-black text-rose-500">{kpi.messageCounts["Poor"] || 0}</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Progress Over Time */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="progress_over_time" />
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase mb-4 w-4/5">Progress Over Time</h2>
                        <div className="h-72">
                            <Line
                                data={{
                                    labels: analytics.trend.labels,
                                    datasets: [
                                        { label: 'Avg Score (L)', yAxisID: 'y', data: analytics.trend.scores, borderColor: '#4f46e5', backgroundColor: '#4f46e5', tension: 0.4, borderWidth: 3 },
                                        { label: 'Avg Speed', yAxisID: 'y1', data: analytics.trend.speeds, borderColor: '#14b8a6', backgroundColor: '#14b8a6', tension: 0.4, borderWidth: 3 }
                                    ]
                                }}
                                options={{
                                    ...commonChartOptions,
                                    scales: {
                                        x: { grid: { display: false } },
                                        y: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Lexile Score', font: { weight: 'bold' } } },
                                        y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Reading Speed', font: { weight: 'bold' } } }
                                    }
                                }}
                            />
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative">
                        <ExportBtn data={analytics.exportData.improvementData} filename="improvement_rates" />
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase mb-4 w-4/5">Improvement Rates (%)</h2>
                        <div className="h-72">
                            <Bar
                                data={{
                                    labels: analytics.improvementCompare.labels,
                                    datasets: [
                                        { label: '% Improved Score', data: analytics.improvementCompare.scoreRates, backgroundColor: '#3b82f6', borderRadius: 4 },
                                        { label: '% Improved Speed', data: analytics.improvementCompare.speedRates, backgroundColor: '#8b5cf6', borderRadius: 4 }
                                    ]
                                }}
                                options={{
                                    ...commonChartOptions,
                                    scales: {
                                        y: { suggestedMax: 100, title: { display: true, text: '% of Students Improved' } },
                                        x: { grid: { display: false } }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Compare Avg Metrics By School */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="avg_metrics_by_school" />
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase mb-4 w-4/5">Average Metrics by School</h2>
                        <div className="h-72">
                            <Bar
                                data={{
                                    labels: analytics.schoolCompare.labels,
                                    datasets: [
                                        { label: 'Avg Lexile Score', data: analytics.schoolCompare.scores, backgroundColor: '#6366f1', borderRadius: 6 },
                                        { label: 'Avg Reading Speed', data: analytics.schoolCompare.speeds, backgroundColor: '#2dd4bf', borderRadius: 6 }
                                    ]
                                }}
                                options={commonChartOptions}
                            />
                        </div>
                    </div>

                    {/* Student Growth Scatter Plot */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative">
                        <ExportBtn data={analytics.exportData.scatterGrowth} filename="student_growth_scatter" />
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase mb-4 w-4/5">Student Growth (Prev vs Current Score)</h2>
                        <div className="h-72">
                            <Scatter
                                data={{
                                    datasets: [{
                                        label: 'Students Growth Trajectory',
                                        data: analytics.distributions.scatterGrowth,
                                        backgroundColor: 'rgba(236, 72, 153, 0.6)',
                                        borderColor: '#ec4899',
                                        pointRadius: 5
                                    }]
                                }}
                                options={{
                                    ...commonChartOptions,
                                    // plugins: { tooltip: { callbacks: { label: (ctx) => `${ctx.raw.name}: Prev ${ctx.raw.x}L -> Curr ${ctx.raw.y}L` } } },
                                    plugins: { tooltip: { callbacks: { label: (ctx) => `${ctx.raw.erpId}: Prev ${ctx.raw.x}L -> Curr ${ctx.raw.y}L` } } },
                                    scales: {
                                        x: { title: { display: true, text: 'Previous Lexile Score' } },
                                        y: { title: { display: true, text: 'Current Lexile Score' } }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    {/* Stacked Reader Messages By School */}
                    <div className="col-span-1 bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="reader_status_by_school" />
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase mb-4 w-4/5">Reader Status by School</h2>
                        <div className="h-72">
                            <Bar
                                data={{
                                    labels: analytics.schoolCompare.labels,
                                    datasets: [
                                        { label: 'Outstanding', data: analytics.schoolCompare.labels.map(l => analytics.schoolCompare.raw[l].messages["Outstanding Reader"] || 0), backgroundColor: '#4f46e5', stack: 'Stack 0' },
                                        { label: 'Very Good', data: analytics.schoolCompare.labels.map(l => analytics.schoolCompare.raw[l].messages["Very Good Reader"] || 0), backgroundColor: '#3b82f6', stack: 'Stack 0' },
                                        { label: 'Good', data: analytics.schoolCompare.labels.map(l => analytics.schoolCompare.raw[l].messages["Good Reader"] || 0), backgroundColor: '#10b981', stack: 'Stack 0' },
                                        { label: 'Needs Impr.', data: analytics.schoolCompare.labels.map(l => analytics.schoolCompare.raw[l].messages["Reading needs improvement"] || 0), backgroundColor: '#f59e0b', stack: 'Stack 0' },
                                        { label: 'Prioritized', data: analytics.schoolCompare.labels.map(l => analytics.schoolCompare.raw[l].messages["Reading to be Prioritized"] || 0), backgroundColor: '#f97316', stack: 'Stack 0' },
                                        { label: 'Poor', data: analytics.schoolCompare.labels.map(l => analytics.schoolCompare.raw[l].messages["Poor"] || 0), backgroundColor: '#ef4444', stack: 'Stack 0' }
                                    ]
                                }}
                                options={{
                                    ...commonChartOptions,
                                    scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, border: { display: false } } }
                                }}
                            />
                        </div>
                    </div>

                    {/* Book Matching Bubble Scatter */}
                    <div className="col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="book_selection_matching" />
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase mb-4 w-4/5">Book Selection Matching (Score vs Paragraph Lexile)</h2>
                        <div className="h-72">
                            <Bubble
                                data={{
                                    datasets: [{
                                        label: 'Student Selection',
                                        data: analytics.distributions.bookMatchingScatter,
                                        backgroundColor: 'rgba(99, 102, 241, 0.6)',
                                        borderColor: '#4f46e5',
                                    }]
                                }}
                                options={{
                                    ...commonChartOptions,
                                    plugins: {
                                        tooltip: {
                                            callbacks: {
                                                // label: (ctx) => `${ctx.raw.name}: Student(${ctx.raw.x}L), Book(${ctx.raw.y}L)`
                                                label: (ctx) => `${ctx.raw.erpId}: Student(${ctx.raw.x}L), Book(${ctx.raw.y}L)`
                                            }
                                        }
                                    },
                                    scales: {
                                        x: { title: { display: true, text: 'Student Lexile Score' } },
                                        y: { title: { display: true, text: 'Selected Book Lexile' } }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Histogram Distribution */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="lexile_score_distribution" />
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase mb-1 w-4/5">Lexile Score Distribution</h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mb-4">Student Count per bracket</p>
                        <div className="h-72">
                            <Bar
                                data={{
                                    labels: Object.keys(analytics.distributions.histogramBins),
                                    datasets: [{
                                        label: 'Number of Students',
                                        data: Object.values(analytics.distributions.histogramBins),
                                        backgroundColor: '#a855f7',
                                        borderRadius: 8
                                    }]
                                }}
                                options={commonChartOptions}
                            />
                        </div>
                    </div>

                    {/* Level vs Speed Scatter */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 relative">
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="level_vs_speed_matrix" />
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase mb-1 w-4/5">Level vs Speed Matrix</h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mb-4">Identify Outliers easily</p>
                        <div className="h-72">
                            <Scatter
                                data={{
                                    datasets: [{
                                        label: 'Students',
                                        data: analytics.distributions.scatterSpeedLevel,
                                        backgroundColor: (ctx) => {
                                            const msg = ctx.raw?.msg;
                                            if (msg === 'Good Reader') return 'rgba(16, 185, 129, 0.7)';
                                            if (msg === 'Poor') return 'rgba(239, 68, 68, 0.7)';
                                            return 'rgba(245, 158, 11, 0.7)';
                                        },
                                        borderColor: '#fff',
                                        pointRadius: 6,
                                        pointHoverRadius: 8
                                    }]
                                }}
                                options={{
                                    ...commonChartOptions,
                                    // plugins: { tooltip: { callbacks: { label: (ctx) => `${ctx.raw.name}: Speed ${ctx.raw.x}, Score ${ctx.raw.y}L` } } },
                                    plugins: { tooltip: { callbacks: { label: (ctx) => `${ctx.raw.erpId}: Speed ${ctx.raw.x}, Score ${ctx.raw.y}L` } } },
                                    scales: {
                                        x: { title: { display: true, text: 'Reading Speed' } },
                                        y: { title: { display: true, text: 'Lexile Score' } }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Heatmap Proxy: Table showing average score by class and school */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-10 relative">
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                        <h2 className="text-lg font-black text-slate-800 tracking-tight uppercase w-4/5">Average Level: Class vs School Matrix</h2>
                        <ExportBtn data={analytics.exportData.baseAssessments} filename="heatmap_class_school_matrix" className="" />
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr>
                                    <th className="px-6 py-4 bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-r border-slate-200">Class</th>
                                    {filterOptions.schools.map(sch => (
                                        <th key={sch} className="px-6 py-4 bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-r border-slate-200 text-center">{sch}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(analytics.heatmapMatrix).sort().map(cls => (
                                    <tr key={cls} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4 border-b border-r border-slate-100 font-bold text-slate-700 text-sm">{cls}</td>
                                        {filterOptions.schools.map(sch => {
                                            const cell = analytics.heatmapMatrix[cls][sch];
                                            const avg = cell && cell.count > 0 ? Math.round(cell.sum / cell.count) : 0;

                                            // dynamically style background color based on score magnitude
                                            let bgClass = "bg-slate-50";
                                            if (avg > 400) bgClass = "bg-indigo-600 text-white";
                                            else if (avg > 250) bgClass = "bg-indigo-300 text-indigo-900";
                                            else if (avg > 100) bgClass = "bg-indigo-100 text-indigo-700";
                                            else if (avg > 0) bgClass = "bg-slate-100 text-slate-500";

                                            return (
                                                <td key={sch} className={`px-6 py-4 border-b border-r border-slate-100 text-center font-black transition-colors ${bgClass}`}>
                                                    {avg > 0 ? `${avg}L` : '-'}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Dashboard;